import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Button, Dropdown, Form, Spinner } from 'react-bootstrap'
import clsx from 'clsx'
import { toast } from 'react-toastify'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useDebounce } from '../../../../../_metronic/helpers'
import {
  useCompleteTaskMutation,
  useCompleteMultipleTasksMutation,
  useCreateTaskMutation,
  useDeleteMultipleTasksMutation,
  useDeleteTaskMutation,
  useGetTaskByIdQuery,
  useGetTaskStatsQuery,
  useGetTasksQuery,
  useUpdateTaskMutation,
} from '../../../../../services/cellcast.v2'
import {
  GetTaskStatsParams,
  GetTasksParams,
  TaskItem,
  TaskStatus,
  TaskPriority,
} from '../../../../../services/cellcast.v2.types'
import DeleteConfirmationPopup from '../../../../Components/DeleteConfirmationPopup'
import { getAllUserDetails } from '../../accounts/Overview/OverviewSlice/UserDetailsSlice'
import TaskFormModal, { TaskFormValues } from './TaskFormModal'
import DataTable, {
  Column,
} from '../../../../Components/shared/data-table/src/app/Components/shared/data-table/table'
import {
  GridSortModel,
  GridSortItem,
} from '../../../../Components/shared/data-table/src/app/Components/shared/data-table/pagination.types'
import { useResponsive } from '../../../../hooks/useResponsive'
import { TaskMobileCard } from './components/TaskMobileCard'
import { formatDateTime, resolveContactLabel, formatTaskInitials, STATUS_META } from './taskHelpers'
import './Tasks.scss'
import { GetAllTeamList } from '../../accounts/Overview/OverviewSlice/TeamListSlice'

type QuickFilter = 'all' | 'due-today' | 'overdue' | 'upcoming' | 'completed'
type StatusFilter = 'all' | TaskStatus
type PriorityFilter = 'all' | TaskPriority

export interface TaskItemWithId extends TaskItem {
  id: string
  index?: number
}

const QUICK_FILTERS: Array<{ value: QuickFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'due-today', label: 'Due Today' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'completed', label: 'Completed' },
]

const QUICK_FILTER_ICONS: Record<QuickFilter, string> = {
  all: 'bi-list',
  'due-today': 'bi-list',
  overdue: 'bi-list',
  upcoming: 'bi-list',
  completed: 'bi-check2-circle',
}

const STATUS_VALUES = {
  ALL: 'all',
  PENDING: 'pending',
  COMPLETED: 'completed',
} as const

const STATUS_FILTERS: Array<{ value: StatusFilter; label: string }> = [
  { value: STATUS_VALUES.ALL, label: 'Status: All' },
  { value: STATUS_VALUES.PENDING, label: 'Pending' },
  { value: STATUS_VALUES.COMPLETED, label: 'Completed' },
]

const PRIORITY_FILTERS: Array<{ value: PriorityFilter; label: string }> = [
  { value: 'all', label: 'Priority: All' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
]

const SORT_OPTIONS: Array<{
  id: string
  label: string
  sortBy: string
  sortOrder: 'asc' | 'desc'
}> = [
    { id: 'dueDate-asc', label: 'Due date ↑', sortBy: 'dueDate', sortOrder: 'asc' },
    { id: 'dueDate-desc', label: 'Due date ↓', sortBy: 'dueDate', sortOrder: 'desc' },
    { id: 'createdAt-desc', label: 'Recently created', sortBy: 'createdAt', sortOrder: 'desc' },
    { id: 'createdAt-asc', label: 'Oldest created', sortBy: 'createdAt', sortOrder: 'asc' },
  ]

const STATUS_FILTERS_PENDING = [
  'due-today',
  'overdue',
  'upcoming',
]

const STATUS_FILTERS_COMPLETED = [
  'completed',
]

const PAGE_SIZES = [10, 25, 50]

const EMPTY_TASKS: TaskItem[] = []

type TeamMember = {
  _id?: string
  id?: string
  name?: string
  fullName?: string
  email?: string
  userId?: string
}

type RootState = {
  teamlist?: {
    TeamList?: {
      items?: TeamMember[]
    }
  }
  userDetails?: {
    userDetails?: {
      data?: {
        user?: TeamMember
      }
    }
  }
}

const CONTACT_COLOR_CLASSES = [
  'bg-light-primary text-primary',
  'bg-light-success text-success',
  'bg-light-warning text-warning',
  'bg-light-info text-info',
  'bg-light-danger text-danger',
  'bg-light-dark text-dark',
]

const circleBadgeStyle = {
  width: '32px',
  height: '32px',
  minWidth: '32px',
  minHeight: '32px',
  fontSize: '0.75rem',
  flexShrink: 0,
} as const

const MAX_VISIBLE_CONTACTS = 2

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const resolveErrorMessage = (error: unknown): string => {
  if (!isRecord(error)) {
    return 'Something went wrong'
  }

  const dataField = error.data
  if (isRecord(dataField)) {
    const message = dataField.message
    if (typeof message === 'string' && message.trim().length > 0) {
      return message.trim()
    }
    const nested = dataField.data
    if (isRecord(nested)) {
      const serverError = nested.serverError
      if (typeof serverError === 'string' && serverError.trim().length > 0) {
        return serverError.trim()
      }
    }
  }

  if ('error' in error && typeof error.error === 'string' && error.error.trim().length > 0) {
    return error.error.trim()
  }

  if ('message' in error && typeof error.message === 'string' && error.message.trim().length > 0) {
    return error.message.trim()
  }

  return 'Unable to complete the request'
}

const Tasks: React.FC = () => {
  const navigate = useNavigate()
  const {isMobile} = useResponsive()
  const [searchParams] = useSearchParams()
  const dispatch = useDispatch()

  const urlTaskId = searchParams.get('id')
  const urlCategory = searchParams.get('category')

  const [page, setPage] = useState<number>(0)
  const [limit, setLimit] = useState<number>(PAGE_SIZES[0])
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [quickFilter, setQuickFilter] = useState<QuickFilter>('all')
  const [priorityFilter, setPriorityFilter] = useState<PriorityFilter>('all')
  const [sortData, setSortData] = useState<GridSortModel>({ field: 'dueDate', sort: 'asc' })
  const [assigneeFilter, setAssigneeFilter] = useState<string>('any')
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([])
  const [showTaskForm, setShowTaskForm] = useState(false)
  const [showManageFieldsModal, setShowManageFieldsModal] = useState(false)
  const [pendingFieldSelection, setPendingFieldSelection] = useState<string[]>([])
  const [editingTask, setEditingTask] = useState<TaskItem | null>(null)
  const [rowActionTaskId, setRowActionTaskId] = useState<string | null>(null)
  const [hasOpenedFromUrl, setHasOpenedFromUrl] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteContext, setDeleteContext] = useState<{
    ids: string[]
    label: string
    isBulk: boolean
  }>({ ids: [], label: '', isBulk: false })
  const [showCompletedSection, setShowCompletedSection] = useState(false)
  const currentUserDetails = useSelector<RootState, TeamMember | undefined>((state) => {
    return getAllUserDetails(state) as TeamMember | undefined
  })

  const teamMembers = useSelector<RootState, TeamMember[]>((state) => {
    const teamData = state.teamlist?.TeamList?.items ?? []
    const user = state.userDetails?.userDetails?.data?.user
    const combined = user ? [user, ...teamData] : teamData
    const unique = new Map<string, TeamMember>()
    combined.forEach((member) => {
      const id = member?._id || member?.id
      if (id && !unique.has(id)) {
        unique.set(id, member)
      }
    })
    return Array.from(unique.values())
  })

  const currentUserId = useMemo(() => {
    if (!currentUserDetails || typeof currentUserDetails !== 'object') {
      return ''
    }

    const idFields = ['_id', 'id', 'userId'] as const

    for (const field of idFields) {
      const value = currentUserDetails[field]
      if (typeof value === 'string' && value.length > 0) {
        return value
      }
    }

    return ''
  }, [currentUserDetails])


  const canQueryTasks = useMemo(() => currentUserId.length > 0, [currentUserId])

  const DEBOUNCE_DELAY = 400
  const debouncedSearch = useDebounce(search, DEBOUNCE_DELAY)

  const apiQuickFilter = quickFilter

  const queryArgs = useMemo<GetTasksParams>(
    () => ({
      page: page + 1,
      limit,
      search: debouncedSearch ? debouncedSearch : undefined,
      status: statusFilter === STATUS_VALUES.ALL ? undefined : statusFilter,
      priority: priorityFilter === 'all' ? undefined : priorityFilter,
      assignedTo: assigneeFilter === 'any' ? undefined : assigneeFilter,
      sortBy: sortData.field,
      sortOrder: sortData.sort === 'asc' ? 'asc' : 'desc',
      quickFilter: apiQuickFilter,
      user: currentUserId || undefined,
    }),
    [
      page,
      limit,
      debouncedSearch,
      statusFilter,
      priorityFilter,
      assigneeFilter,
      sortData.field,
      sortData.sort,
      apiQuickFilter,
      currentUserId,
    ]
  )

  const {
    data: tasksResponse,
    error: tasksError,
    isFetching: isTasksFetching,
    refetch: refetchTasks,
  } = useGetTasksQuery(queryArgs, {
    skip: !canQueryTasks,
    refetchOnMountOrArgChange: true,
  })

  const statsQueryArgs = useMemo<GetTaskStatsParams>(
    () => ({
      user: currentUserId || undefined,
    }),
    [currentUserId]
  )

  const {
    data: statsResponse,
    isFetching: isStatsFetching,
    refetch: refetchStats,
  } = useGetTaskStatsQuery(statsQueryArgs, {
    skip: !canQueryTasks,
    refetchOnMountOrArgChange: true,
  })

  const {
    data: taskByIdResponse,
    isFetching: isTaskByIdFetching
  } = useGetTaskByIdQuery(urlTaskId || '', {
    skip: !urlTaskId || urlCategory !== 'tasks',
  })

  const [createTask, { isLoading: isCreating }] = useCreateTaskMutation()
  const [completeTask, { isLoading: isCompleting }] = useCompleteTaskMutation()
  const [updateTask, { isLoading: isUpdating }] = useUpdateTaskMutation()
  const [deleteTask, { isLoading: isDeletingSingle }] = useDeleteTaskMutation()
  const [deleteMultipleTasks, { isLoading: isBulkDeleting }] = useDeleteMultipleTasksMutation()
  const [completeMultipleTasks, { isLoading: isBulkCompleting }] = useCompleteMultipleTasksMutation()

  const tasksMeta = tasksResponse?.data
  const taskItems = tasksMeta?.items
  const tasks: TaskItem[] = taskItems ?? EMPTY_TASKS
  const totalTasks = tasksMeta?.total ?? 0

  useEffect(() => {
    if (!isTasksFetching && tasks.length === 0 && page > 0) {
      setPage(0)
    }
    if (currentUserId) {
      dispatch(GetAllTeamList({ownerId: currentUserId}))
    }
  }, [tasks, isTasksFetching, page])


  useEffect(() => {
    setPage(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    quickFilter,
    statusFilter,
    priorityFilter,
    assigneeFilter,
    debouncedSearch,
    sortData.field,
    sortData.sort,
    limit,
    currentUserId,
  ])

  useEffect(() => {
    setSelectedTaskIds([])
  }, [page, quickFilter, statusFilter, priorityFilter, assigneeFilter, limit, currentUserId])

  useEffect(() => {
    if (urlTaskId && urlCategory === 'tasks' && !showTaskForm && !hasOpenedFromUrl) {
      const taskFromApi = taskByIdResponse?.data
      if (taskFromApi) {
        setEditingTask(taskFromApi)
        setShowTaskForm(true)
        setHasOpenedFromUrl(true)
      }
    }
  }, [urlTaskId, urlCategory, taskByIdResponse, showTaskForm, hasOpenedFromUrl])

  const stats = statsResponse?.data

  const quickFilterItems = useMemo(
    () =>
      QUICK_FILTERS.map((filter) => {
        let count: number | undefined
        switch (filter.value) {
          case 'all':
            count = stats?.totalTasks
            break
          case 'due-today':
            count = stats?.dueTodayTasks
            break
          case 'overdue':
            count = stats?.overdueTasks
            break
          case 'upcoming':
            count = stats?.upcomingTasks
            break
          case 'completed':
            count = stats?.completedTasks
            break
          default:
            count = undefined
        }
        return { ...filter, count }
      }),
    [stats]
  )

  const selectedSortOption = useMemo(() => {
    return (
      SORT_OPTIONS.find(
        (option) => option.sortBy === sortData.field && option.sortOrder === sortData.sort
      ) ?? SORT_OPTIONS[0]
    )
  }, [sortData.field, sortData.sort])

  const selectedPendingTaskIds = useMemo(() => {
    if (selectedTaskIds.length === 0) {
      return []
    }
    const pendingIds = new Set(
      tasks
        .filter((task) => task.status === STATUS_VALUES.PENDING)
        .map((task) => task.id || task._id)
        .filter((id): id is string => Boolean(id))
    )
    return selectedTaskIds.filter((id) => pendingIds.has(id))
  }, [selectedTaskIds, tasks])

  const hasSelectedTasks = selectedTaskIds.length > 0
  const hasPendingSelected = selectedPendingTaskIds.length > 0

  const canBulkDelete = hasSelectedTasks
  const canBulkComplete = hasSelectedTasks

  const isTableLoading = isTasksFetching

  const formattedTotalTasks = useMemo(
    () => new Intl.NumberFormat().format(totalTasks),
    [totalTasks]
  )

  const taskRows: TaskItemWithId[] = tasks.map((task, idx) => ({
    ...task,
    id: task.id || task._id || idx.toString(),
    index: page * limit + idx + 1,
  }))

  const isCompletedTab = statusFilter === STATUS_VALUES.COMPLETED

  const displayedTaskRows = taskRows

  const allCurrentIds = useMemo(
    () =>
      displayedTaskRows
        .map((task) => task.id)
        .filter((value): value is string => Boolean(value)),
    [displayedTaskRows]
  )

  const isAllSelected =
    allCurrentIds.length > 0 && allCurrentIds.every((id) => selectedTaskIds.includes(id))

  const handleToggleQuickFilter = (value: QuickFilter) => {
    setQuickFilter(value)

    if (value === 'completed') {
      setStatusFilter(STATUS_VALUES.COMPLETED)
    } else if (value === 'all') {
      setStatusFilter(STATUS_VALUES.ALL)
    } else {
      // 'due-today', 'overdue', 'upcoming' -> set to pending
      setStatusFilter(STATUS_VALUES.PENDING)
    }
  }

  const handleSelectTask = (taskId: string) => {
    setSelectedTaskIds((prev) =>
      prev.includes(taskId) ? prev.filter((id) => id !== taskId) : [...prev, taskId]
    )
  }

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedTaskIds((prev) => prev.filter((id) => !allCurrentIds.includes(id)))
    } else {
      setSelectedTaskIds(allCurrentIds)
    }
  }

  const handlePageChange = (newPage: number) => {
    setPage(newPage)
  }

  const handlePageSizeChange = (newPageSize: number) => {
    setLimit(newPageSize)
    setPage(0)
  }

  const handleSortChange = (sortItem: GridSortItem) => {
    setSortData({ field: sortItem.field, sort: sortItem.sort ?? 'desc' })
  }

  const closeTaskForm = useCallback(() => {
    setShowTaskForm(false)
    setEditingTask(null)
    setHasOpenedFromUrl(false)

    const currentParams = new URLSearchParams(window.location.search)
    if (currentParams.has('id') || currentParams.has('category')) {
      currentParams.delete('id')
      currentParams.delete('category')
      const newSearch = currentParams.toString()
      navigate({ search: newSearch ? `?${newSearch}` : '' }, { replace: true })
    }
  }, [navigate])

  const handleCreateClick = () => {
    setEditingTask(null)
    setShowTaskForm(true)
    setHasOpenedFromUrl(false)

    const currentParams = new URLSearchParams(window.location.search)
    if (currentParams.has('id') || currentParams.has('category')) {
      currentParams.delete('id')
      currentParams.delete('category')
      const newSearch = currentParams.toString()
      navigate({ search: newSearch ? `?${newSearch}` : '' }, { replace: true })
    }
  }

  const handleEditClick = (task: TaskItem | TaskItemWithId) => {
    const taskId = task.id || task._id || ''
    setEditingTask(task)
    setShowTaskForm(true)
    if (taskId) {
      const currentParams = new URLSearchParams(window.location.search)
      currentParams.set('category', 'tasks')
      currentParams.set('id', taskId)
      navigate({ search: `?${currentParams.toString()}` }, { replace: true })
    }
  }

  const handleDuplicateTask = (task: TaskItem | TaskItemWithId) => {
    const duplicatedTask: Partial<TaskItem> = {
      title: `Copy of ${task.title}`,
      description: task.description,
      status: 'pending' as TaskStatus,
      dueDate: task.dueDate,
      priority: task.priority,
      setUpReminder: task.setUpReminder,
      reminderMinutesBeforeDue: task.reminderMinutesBeforeDue,
      assignedTo: task.assignedTo,
      contacts: task.contacts,
    }


    setEditingTask(duplicatedTask as TaskItem | null)
    setShowTaskForm(true)
    setHasOpenedFromUrl(false)

    const currentParams = new URLSearchParams(window.location.search)
    if (currentParams.has('id') || currentParams.has('category')) {
      currentParams.delete('id')
      currentParams.delete('category')
      const newSearch = currentParams.toString()
      navigate({ search: newSearch ? `?${newSearch}` : '' }, { replace: true })
    }
  }

  const refreshData = useCallback(async () => {
    if (!canQueryTasks) {
      return
    }
    await Promise.all([refetchTasks(), refetchStats()])
  }, [canQueryTasks, refetchTasks, refetchStats])

  const handleSubmitTask = async (values: TaskFormValues) => {
    const sanitized = { ...values }
    if (!sanitized.description) {
      delete sanitized.description
    }
    if (!sanitized.assignedTo) {
      delete sanitized.assignedTo
    }
    if (!sanitized.contacts || sanitized.contacts.length === 0) {
      delete sanitized.contacts
    }
    if (!sanitized.setUpReminder) {
      delete sanitized.reminderMinutesBeforeDue
    }

    try {
      if (editingTask && (editingTask.id || editingTask._id)) {
        const taskId = editingTask.id || editingTask._id
        if (taskId) {
          await updateTask({ id: taskId, data: sanitized }).unwrap()
          toast.success('Task updated successfully')
        }
      } else {
        await createTask(sanitized).unwrap()
        toast.success('Task created successfully')
      }

      setShowTaskForm(false)
      setEditingTask(null)
      setHasOpenedFromUrl(false)

      const currentParams = new URLSearchParams(window.location.search)
      if (currentParams.has('id') || currentParams.has('category')) {
        currentParams.delete('id')
        currentParams.delete('category')
        const newSearch = currentParams.toString()
        navigate({ search: newSearch ? `?${newSearch}` : '' }, { replace: true })
      }

      closeTaskForm()
      await refreshData()
    } catch (error) {
      toast.error(resolveErrorMessage(error))
      throw error
    }
  }

  const renderPriorityLabel = (priority?: TaskPriority | string | null) => {
    const normalized = (priority || 'unknown').toString().toLowerCase()
    const label = normalized.charAt(0).toUpperCase() + normalized.slice(1)
    return (
      <span className={clsx('task-priority-badge', `task-priority-badge--${normalized}`)}>
        {label}
      </span>
    )
  }

  const renderBooleanLabel = (value?: boolean | null) => {
    if (value === undefined || value === null) {
      return <span className='text-muted'>—</span>
    }
    return (
      <span
        className={clsx(
          'task-boolean-badge',
          value ? 'task-boolean-badge--yes' : 'task-boolean-badge--no'
        )}
      >
        {value ? 'Yes' : 'No'}
      </span>
    )
  }

  const renderUserLabel = (user?: TaskItem['assignedTo'] | null) => {
    if (!user) return <span className='text-muted'>—</span>
    const label = user.name || user.email
    return <span className='fw-semibold text-gray-900'>{label}</span>
  }

  type OptionalColumnDefinition = {
    selectable: boolean
    column: Column<TaskItemWithId>
  }

  const OPTIONAL_COLUMN_DEFINITIONS = useMemo(
    () =>
      new Map<string, OptionalColumnDefinition>([
        [
          'description',
          {
            selectable: true,
            column: {
              id: 'description',
              label: 'Description',
              width: 260,
              Cell: (row) =>
                row.description ? (
                  <span className='text-muted'>
                    {row.description.length > 120
                      ? `${row.description.slice(0, 120)}…`
                      : row.description}
                  </span>
                ) : (
                  <span className='text-muted'>—</span>
                ),
            },
          },
        ],
        [
          'contacts',
          {
            selectable: true,
            column: {
              id: 'contacts',
              label: 'Associated Contacts',
              width: 260,
              className: 'task-contacts-column',
              Cell: (row) => {
                const contacts = Array.isArray(row.contacts) ? row.contacts : []
                if (contacts.length === 0) {
                  return <span className='text-muted'>—</span>
                }

                const visibleContacts = contacts.slice(0, MAX_VISIBLE_CONTACTS)
                const remaining = contacts.length - MAX_VISIBLE_CONTACTS

                return (
                  <div className='task-contacts-pills d-flex flex-wrap align-items-center gap-2'>
                    {visibleContacts.map((contact, index) => {
                      const label = resolveContactLabel(contact)
                      return (
                        <div
                          key={`${row.id}-contact-${index}`}
                          className='d-inline-flex align-items-center gap-2 border rounded-pill px-3 py-1 bg-light'
                          title={label}
                        >
                          <span
                            className={clsx(
                              'rounded-circle d-inline-flex align-items-center justify-content-center fw-semibold text-uppercase flex-shrink-0',
                              CONTACT_COLOR_CLASSES[index % CONTACT_COLOR_CLASSES.length]
                            )}
                            style={circleBadgeStyle}
                          >
                            {formatTaskInitials(label)}
                          </span>
                          <span className='fw-semibold text-gray-900 text-truncate' style={{ minWidth: 0 }}>{label}</span>
                        </div>
                      )
                    })}
                    {remaining > 0 && (
                      <div className='task-contacts-more rounded-pill bg-light text-gray-700 fw-semibold px-3 py-1'>
                        +{remaining}
                      </div>
                    )}
                  </div>
                )
              },
            },
          },
        ],
        [
          'priority',
          {
            selectable: true,
            column: {
              id: 'priority',
              label: 'Priority',
              width: 120,
              Cell: (row) => renderPriorityLabel(row.priority),
            },
          },
        ],
        [
          'completedAt',
          {
            selectable: true,
            column: {
              id: 'completedAt',
              label: 'Completed At',
              width: 180,
              Cell: (row) => <span>{formatDateTime(row.completedAt ?? undefined)}</span>,
            },
          },
        ],
        [
          'completedBy',
          {
            selectable: true,
            column: {
              id: 'completedBy',
              label: 'Completed By',
              width: 180,
              Cell: (row) => renderUserLabel(row.completedBy),
            },
          },
        ],
        [
          'notificationSent',
          {
            selectable: true,
            column: {
              id: 'notificationSent',
              label: 'Notification Sent',
              width: 160,
              Cell: (row) => renderBooleanLabel(row.notificationSent),
            },
          },
        ],
        [
          'reminderNotificationSent',
          {
            selectable: true,
            column: {
              id: 'reminderNotificationSent',
              label: 'Reminder Sent',
              width: 160,
              Cell: (row) => renderBooleanLabel(row.reminderNotificationSent),
            },
          },
        ],
        [
          'setUpReminder',
          {
            selectable: true,
            column: {
              id: 'setUpReminder',
              label: 'Reminder Enabled',
              width: 160,
              Cell: (row) => renderBooleanLabel(row.setUpReminder),
            },
          },
        ],
      ]),
    []
  )

  const DEFAULT_OPTIONAL_COLUMNS = useMemo(() => ['description', 'contacts', 'priority'], [])

  const [visibleOptionalColumns, setVisibleOptionalColumns] = useState<string[]>(
    Array.from(DEFAULT_OPTIONAL_COLUMNS)
  )

  const handleToggleTaskStatus = async (task: TaskItem | TaskItemWithId) => {
    const taskId = task.id || task._id || ''
    if (!taskId) return
    setRowActionTaskId(taskId)
    try {
      if (task.status === STATUS_VALUES.COMPLETED) {
        await updateTask({ id: taskId, data: { status: STATUS_VALUES.PENDING } }).unwrap()
        toast.success('Task moved back to pending')
      } else {
        await completeTask(taskId).unwrap()
        toast.success('Task marked as completed')
        setSelectedTaskIds((prev) => prev.filter((id) => id !== taskId))
      }
      await refreshData()
    } catch (error) {
      toast.error(resolveErrorMessage(error))
    } finally {
      setRowActionTaskId(null)
    }
  }

  const handleDeleteTask = (task: TaskItem | TaskItemWithId) => {
    const taskId = task.id || task._id || ''
    setDeleteContext({
      ids: [taskId],
      label: `Delete the task "${task.title}"?`,
      isBulk: false,
    })
    setShowDeleteConfirm(true)
  }

  const handleBulkComplete = async () => {
    if (!selectedPendingTaskIds.length) return
    try {
      const response = await completeMultipleTasks({ ids: selectedPendingTaskIds }).unwrap()
      toast.success(
        response?.message || `Marked ${selectedPendingTaskIds.length} task(s) as completed`
      )
      setSelectedTaskIds((prev) => prev.filter((id) => !selectedPendingTaskIds.includes(id)))
      await refreshData()
    } catch (error) {
      toast.error(resolveErrorMessage(error))
    }
  }

  const handleBulkDelete = () => {
    setDeleteContext({
      ids: selectedTaskIds,
      label: `Delete ${selectedTaskIds.length} selected task(s)?`,
      isBulk: true,
    })
    setShowDeleteConfirm(true)
  }

  const confirmDelete = async () => {
    if (!deleteContext.ids.length) return
    try {
      if (deleteContext.isBulk && deleteContext.ids.length > 1) {
        await deleteMultipleTasks({ ids: deleteContext.ids }).unwrap()
        toast.success(`Deleted ${deleteContext.ids.length} tasks`)
      } else {
        setRowActionTaskId(deleteContext.ids[0])
        await deleteTask(deleteContext.ids[0]).unwrap()
        toast.success('Task deleted')
      }
      setSelectedTaskIds((prev) => prev.filter((id) => !deleteContext.ids.includes(id)))
      await refreshData()
    } catch (error) {
      toast.error(resolveErrorMessage(error))
    } finally {
      setRowActionTaskId(null)
      setShowDeleteConfirm(false)
      setDeleteContext({ ids: [], label: '', isBulk: false })
    }
  }

  const closeDeleteConfirmation = () => {
    setShowDeleteConfirm(false)
    setDeleteContext({ ids: [], label: '', isBulk: false })
  }

  const resolveTeamMemberLabel = useCallback((member?: TeamMember) => {
    return member?.name || member?.fullName || member?.email || member?.id || 'Member'
  }, [])

  const assigneeDisplayLabel = useMemo(() => {
    if (assigneeFilter === 'any') {
      return 'Assignee: Any'
    }
    const member = teamMembers.find((item) => {
      const id = item?._id || item?.id
      return id === assigneeFilter
    })
    const label = resolveTeamMemberLabel(member)
    return `${label}`
  }, [assigneeFilter, teamMembers, resolveTeamMemberLabel])

  const isStatusFilterDisabled = useMemo(() => {
    return quickFilter !== 'all'
  }, [quickFilter])

  const statusDisplayLabel = useMemo(() => {
    // When disabled, show the effective status
    if (isStatusFilterDisabled) {
      const option = STATUS_FILTERS.find((item) => item.value === statusFilter)
      return option?.label ?? 'Status: All'
    }
    const option = STATUS_FILTERS.find((item) => item.value === statusFilter)
    return option?.label ?? 'Status: All'
  }, [statusFilter, isStatusFilterDisabled])

  const quickFilterDisplayLabel = useMemo(() => {
    if (quickFilter === 'all') {
      return 'Due Date: Any'
    }
    if (quickFilter === 'completed') {
      return 'Completed Tasks'
    }
    const option = QUICK_FILTERS.find((item) => item.value === quickFilter)
    return option?.label ?? 'Due Date'
  }, [quickFilter])

  const priorityDisplayLabel = useMemo(() => {
    const option = PRIORITY_FILTERS.find((item) => item.value === priorityFilter)
    return option?.label ?? 'Priority: All'
  }, [priorityFilter])

  const optionalColumnInstances = useMemo(() => {
    return visibleOptionalColumns
      .map((columnId) => OPTIONAL_COLUMN_DEFINITIONS.get(columnId)?.column)
      .filter((value): value is Column<TaskItemWithId> => Boolean(value))
  }, [visibleOptionalColumns, OPTIONAL_COLUMN_DEFINITIONS])

  const taskColumns: Column<TaskItemWithId>[] = [
    {
      id: 'select',
      label: (
        <Form.Check
          type='checkbox'
          checked={isAllSelected}
          onChange={(event) => {
            event.stopPropagation()
            handleSelectAll()
          }}
          onClick={(event) => event.stopPropagation()}
          aria-label='Select all tasks on this page'
          className='m-0'
        />
      ),
      width: 70,
      className: 'task-column-select',
      Cell: (row) => {
        const taskId = row.id
        const isSelected = selectedTaskIds.includes(taskId)
        return (
          <Form.Check
            type='checkbox'
            checked={isSelected}
            onChange={() => handleSelectTask(taskId)}
            aria-label={`Select task ${row.title}`}
            onClick={(e) => e.stopPropagation()}
          />
        )
      },
    },
    {
      id: 'status',
      label: 'Status',
      width: 80,
      Cell: (row) => {
        const statusMeta = STATUS_META[row.status]
        return (
          <div className='fw-semibold text-gray-900'>
            {row.status === STATUS_VALUES.PENDING ? (
              <i
                className={clsx('bi fs-4', statusMeta.icon, statusMeta.className)}
                title={statusMeta.label}
              ></i>
            ) : (
              <i className='bi bi-check-circle-fill text-success fs-4'></i>
            )}
          </div>
        )
      },
    },
    {
      id: 'title',
      label: 'Title',
      width: 220,
      Cell: (row) => <div className='fw-semibold text-gray-900'>{row.title}</div>,
    },
    ...optionalColumnInstances,
    {
      id: 'assignedTo',
      label: 'Assignee',
      width: 160,
      Cell: (row) =>
        row.assignedTo ? (
          <div className='d-flex align-items-center gap-2'>
            <span
              className={clsx(
                'rounded-circle d-inline-flex align-items-center justify-content-center fw-semibold text-uppercase bg-light-success text-success flex-shrink-0'
              )}
              style={circleBadgeStyle}
            >
              {formatTaskInitials(row.assignedTo?.name || row.assignedTo?.email)}
            </span>
            <span className='fw-semibold text-gray-900 text-truncate' style={{ minWidth: 0 }}>
              {row.assignedTo?.name || row.assignedTo?.email || 'Assignee'}
            </span>
          </div>
        ) : (
          <span className='text-muted'>Unassigned</span>
        ),
    },
    {
      id: 'dueDate',
      label: 'Due Date',
      width: 170,
      sortable: true,
      Cell: (row) => {
        const overdueClass =
          row.status !== 'completed' && row.dueDate
            ? new Date(row.dueDate) < new Date()
              ? 'text-danger fw-semibold'
              : ''
            : ''
        return <div className={overdueClass}>{formatDateTime(row.dueDate as string)}</div>
      },
    },
    {
      id: 'actions',
      label: 'Actions',
      width: 160,
      Cell: (row: TaskItemWithId) => {
        const taskId = row.id
        const isProcessingRow =
          rowActionTaskId === taskId && (isDeletingSingle || isUpdating || isCompleting)
        return (
          <div className='d-flex justify-content-end gap-2' onClick={(e) => e.stopPropagation()}>
            <Button
              variant='light'
              size='sm'
              className='btn-icon btn-active-light-primary'
              onClick={() => handleEditClick(row)}
              title='Edit task'
            >
              <i className='bi bi-pencil'></i>
            </Button>
            <Button
              variant='light'
              size='sm'
              className='btn-icon btn-active-light-info'
              onClick={() => handleDuplicateTask(row)}
              title='Duplicate task'
            >
              <i className='bi bi-files'></i>
            </Button>
            <Button
              variant='light'
              size='sm'
              className='btn-icon btn-active-light-success'
              disabled={isProcessingRow}
              onClick={() => handleToggleTaskStatus(row)}
              title={row.status === 'completed' ? 'Mark as pending' : 'Mark as completed'}
            >
              {isProcessingRow && (isCompleting || isUpdating) ? (
                <Spinner animation='border' size='sm' />
              ) : row.status === 'completed' ? (
                <i className='bi bi-arrow-counterclockwise'></i>
              ) : (
                <i className='bi bi-check2-circle'></i>
              )}
            </Button>
            <Button
              variant='light'
              size='sm'
              className='btn-icon btn-active-light-danger'
              disabled={isProcessingRow}
              onClick={() => handleDeleteTask(row)}
              title='Delete task'
            >
              {isProcessingRow && isDeletingSingle ? (
                <Spinner animation='border' size='sm' />
              ) : (
                <i className='bi bi-trash'></i>
              )}
            </Button>
          </div>
        )
      },
    },
  ]

  return (
    <div className='lead-management-tasks-card card'>
      <div className='card-header border-0 py-6 px-6'>
        <div className='tasks-header d-flex align-items-center flex-wrap gap-3 w-100'>
          <div className='tasks-header__info d-flex align-items-center gap-3 flex-wrap'>
            <h3 className='card-title fw-bolder fs-2 mb-0'>Tasks</h3>
            <span className='badge bg-light-primary text-primary fw-semibold'>
              {formattedTotalTasks} Tasks
            </span>
          </div>
          <div className='tasks-header__action d-flex justify-content-md-end justify-content-center'>
            <Button
              variant='primary'
              onClick={handleCreateClick}
              className='fw-semibold w-100 w-md-auto'
            >
              <i className='bi bi-plus-lg me-2'></i>
              Add Task
            </Button>
          </div>
        </div>
      </div>

      <div className='border-top border-light px-6'>
        <div className='d-flex overflow-auto py-3'>
          <ul className='nav nav-stretch nav-line-tabs nav-line-tabs-2x border-transparent fs-6 flex-nowrap task-filter-tabs'>
            {quickFilterItems.map((item) => {
              const isActive = quickFilter === item.value
              const countLabel =
                item.count === undefined ? (isStatsFetching ? ' (…)' : '') : ` (${item.count})`
              const iconClass = QUICK_FILTER_ICONS[item.value]
              return (
                <li
                  key={item.value}
                  className='nav-item'
                  onClick={() => handleToggleQuickFilter(item.value)}
                >
                  <div
                    className={clsx(
                      'nav-link me-6 text-active-primary fs-6 cursor-pointer',
                      isActive && 'active fw-semibold'
                    )}
                  >
                    <i className={clsx('bi me-2', iconClass)}></i>
                    {item.label}
                    {countLabel}
                  </div>
                </li>
              )
            })}
          </ul>
        </div>
      </div>

      <div className={`border-top border-light px-6 py-4 ${isMobile ? 'px-4' : ''}`}>
        <div className={`lead-task-toolbar ${isMobile ? 'lead-task-toolbar-mobile' : ''}`}>
          <div className='lead-task-toolbar__left'>
            <div className='lead-task-toolbar__filters'>
              <Dropdown className='filter-dropdown'>
                <Dropdown.Toggle
                  variant='light'
                  size='sm'
                  className='filter-button fw-semibold border d-flex align-items-center gap-2'
                >
                  <i className='bi bi-people-fill'></i>
                  <span>{assigneeDisplayLabel}</span>
                </Dropdown.Toggle>
                <Dropdown.Menu className='filter-dropdown__menu'>
                  <Dropdown.Item
                    active={assigneeFilter === 'any'}
                    onClick={() => setAssigneeFilter('any')}
                  >
                    Assignee: Any
                  </Dropdown.Item>
                  {teamMembers.map((member) => {
                    const optionId = member?._id || member?.id
                    if (!optionId) return null
                    const label = resolveTeamMemberLabel(member)
                    return (
                      <Dropdown.Item
                        key={optionId}
                        active={assigneeFilter === optionId}
                        onClick={() => setAssigneeFilter(optionId)}
                      >
                        {` ${label}`}
                      </Dropdown.Item>
                    )
                  })}
                </Dropdown.Menu>
              </Dropdown>
              <Dropdown className='filter-dropdown'>
                <Dropdown.Toggle
                  variant='light'
                  size='sm'
                  className='filter-button fw-semibold border d-flex align-items-center gap-2'
                  disabled={isStatusFilterDisabled}
                >
                  <i className='bi bi-check2-circle'></i>
                  <span>{statusDisplayLabel}</span>
                </Dropdown.Toggle>
                {!isStatusFilterDisabled && (
                  <Dropdown.Menu className='filter-dropdown__menu'>
                    {STATUS_FILTERS.map((option) => (
                      <Dropdown.Item
                        key={option.value}
                        active={statusFilter === option.value}
                        onClick={() => setStatusFilter(option.value as StatusFilter)}
                      >
                        {option.label}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                )}
              </Dropdown>
              <Dropdown className='filter-dropdown'>
                <Dropdown.Toggle
                  variant='light'
                  size='sm'
                  className='filter-button fw-semibold border d-flex align-items-center gap-2'
                >
                  <i className='bi bi-calendar3'></i>
                  <span>{quickFilterDisplayLabel}</span>
                </Dropdown.Toggle>
                <Dropdown.Menu className='filter-dropdown__menu'>
                  {QUICK_FILTERS.map((option) => (
                    <Dropdown.Item
                      key={option.value}
                      active={quickFilter === option.value}
                      onClick={() => setQuickFilter(option.value)}
                    >
                      {option.value === 'all' ? 'Due Date: Any' : option.label}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
              <Dropdown className='filter-dropdown'>
                <Dropdown.Toggle
                  variant='light'
                  size='sm'
                  className='filter-button fw-semibold border d-flex align-items-center gap-2'
                >
                  <i className='bi bi-flag-fill'></i>
                  <span>{priorityDisplayLabel}</span>
                </Dropdown.Toggle>
                <Dropdown.Menu className='filter-dropdown__menu'>
                  {PRIORITY_FILTERS.map((option) => (
                    <Dropdown.Item
                      key={option.value}
                      active={priorityFilter === option.value}
                      onClick={() => setPriorityFilter(option.value as PriorityFilter)}
                    >
                      {option.label}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
              <Dropdown>
                <Dropdown.Toggle
                  variant='light'
                  size='sm'
                  className='filter-button fw-semibold border d-flex align-items-center gap-2'
                >
                  <i className='bi bi-sort-up'></i>
                  <span className='flex-shrink-0'>{selectedSortOption.label}</span>
                </Dropdown.Toggle>
                <Dropdown.Menu align='end'>
                  {SORT_OPTIONS.map((option) => (
                    <Dropdown.Item
                      key={option.id}
                      active={
                        option.sortBy === sortData.field && option.sortOrder === sortData.sort
                      }
                      onClick={() => {
                        setSortData({ field: option.sortBy, sort: option.sortOrder })
                      }}
                    >
                      {option.label}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
            <div className='lead-task-toolbar__controls'>

              {(canBulkComplete || isBulkCompleting) && (
                <Button
                  variant='light'
                  size='sm'
                  className='filter-button fw-semibold border d-flex align-items-center gap-2'
                  onClick={handleBulkComplete}
                  disabled={!hasPendingSelected || isBulkCompleting}
                >
                  {isBulkCompleting ? (
                    <>
                      <Spinner animation='border' size='sm' className='me-2' />
                      <span>Completing...</span>
                    </>
                  ) : (
                    <>
                      <i className='bi bi-check2-circle'></i>
                      <span>Mark Completed</span>
                    </>
                  )}
                </Button>
              )}
              {(canBulkDelete || isBulkDeleting) && (
                <Button
                  variant='light'
                  size='sm'
                  className='filter-button filter-button--danger fw-semibold border d-flex align-items-center gap-2'
                  onClick={handleBulkDelete}
                  disabled={!canBulkDelete || isBulkDeleting}
                >
                  {isBulkDeleting ? (
                    <>
                      <Spinner animation='border' size='sm' className='me-2' />
                      <span>Removing...</span>
                    </>
                  ) : (
                    <>
                      <i className='bi bi-trash'></i>
                      <span>Delete Selected</span>
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
          <div className='lead-task-toolbar__actions'>
            <Form.Control
              type='search'
              placeholder='Search for task title'
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className='task-search flex-grow-1'
            />
          </div>
        </div>
      </div>

      <div className={`card-body px-0 pt-1 position-relative ${isMobile ? 'px-4' : ''}`}>
        {tasksError && (
          <div className={`${isMobile ? 'px-2' : 'px-6'} pb-3`}>
            <Alert variant='danger' className='mb-0'>
              {resolveErrorMessage(tasksError)}
            </Alert>
          </div>
        )}

        {/* Mobile view: Card layout */}
        {isMobile ? (
          <div className='mobile-tasks-list'>
            {isTableLoading ? (
              <div className='text-center py-5'>
                <Spinner />
              </div>
            ) : displayedTaskRows.length === 0 ? (
              <div className='text-center text-muted py-5'>
                {isCompletedTab ? 'No completed tasks' : 'No pending tasks'}
              </div>
            ) : (
              displayedTaskRows.map((task) => (
                <TaskMobileCard
                  key={task.id}
                  task={task}
                  isSelected={selectedTaskIds.includes(task.id)}
                  onSelect={handleSelectTask}
                  onEdit={handleEditClick}
                  onDuplicate={handleDuplicateTask}
                  onToggleStatus={handleToggleTaskStatus}
                  onDelete={handleDeleteTask}
                  isProcessing={
                    rowActionTaskId === task.id && (isDeletingSingle || isUpdating || isCompleting)
                  }
                />
              ))
            )}
            {/* Mobile pagination */}
            {totalTasks > 0 && (
              <div className='mobile-pagination-wrapper mt-4'>
                <div className='d-flex flex-column gap-3 align-items-stretch'>
                  <div className='w-100'>
                    <select
                      className='form-select form-select-sm w-100'
                      value={limit}
                      onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                      aria-label='Rows per page'
                    >
                      {PAGE_SIZES.map((size) => (
                        <option key={size} value={size}>
                          {size} per page
                        </option>
                      ))}
                    </select>
                  </div>
                  <span className='text-muted text-center'>
                    Showing {page * limit + 1} - {Math.min((page + 1) * limit, totalTasks)} of {totalTasks}
                  </span>
                  <div className='d-flex align-items-center gap-2 w-100'>
                    <Button
                      variant='light'
                      size='sm'
                      className='flex-grow-1'
                      disabled={page === 0}
                      onClick={() => handlePageChange(page - 1)}
                    >
                      Previous
                    </Button>
                    <span className='text-muted text-center px-2 flex-grow-1'>
                      Page {page + 1} of {Math.ceil(totalTasks / limit) || 1}
                    </span>
                    <Button
                      variant='light'
                      size='sm'
                      className='flex-grow-1'
                      disabled={page >= Math.ceil(totalTasks / limit) - 1}
                      onClick={() => handlePageChange(page + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Desktop view: Table layout */
          <div className='table-responsive lead-management-tasks-table'>
            <DataTable
              items={displayedTaskRows}
              isLoading={isTableLoading}
              columns={taskColumns}
              sortData={sortData}
              handleSortedChange={handleSortChange}
              pageSize={limit}
              totalData={totalTasks}
              handleChangePage={handlePageChange}
              handleChangSizeData={handlePageSizeChange}
              page={page}
              onRowClick={(item) => handleEditClick(item)}
              onRowHover={(item, isHovering) => { }}
            />
          </div>
        )}

      </div>

      <TaskFormModal
        show={showTaskForm}
        onClose={closeTaskForm}
        onSubmit={handleSubmitTask}
        isSubmitting={isCreating || isUpdating}
        initialTask={editingTask ?? undefined}
      />

      <DeleteConfirmationPopup
        show={showDeleteConfirm}
        handleClose={closeDeleteConfirmation}
        Message={deleteContext.label || 'Delete selected task(s)?'}
        confirm={confirmDelete}
      />
    </div>
  )
}

export default Tasks
